/**
 * A geometria do mapa da ilha: onde cada região e cada área caem, em pixel.
 *
 * <p>Morava dentro do `MundoMapa`, e ficou sem dono no dia em que uma segunda tela
 * precisou desenhar o mesmo mapa — a de Áreas e Tier. Copiar as constantes teria
 * produzido dois mapas que começam idênticos e divergem na primeira vez que alguém
 * mover uma região: é a mesma armadilha da régua de progresso, que já disse 16/49
 * contra 16/21.
 *
 * <p>A matemática vem do protótipo, que já a tinha validada. Hexágono de ponta para
 * cima, coordenada axial, sete regiões de sete.
 */

/** Raio do hexágono, em unidades do SVG. */
export const LADO = 150;

/**
 * Centro axial de cada uma das 7 regiões, na ordem em que o time as numera
 * (decisão do Theo): a 1ª ao centro, a 2ª logo abaixo, e daí em diante no sentido
 * anti-horário — baixo, direita, topo, esquerda.
 *
 * <p>Esta ordem <b>é</b> a ordem das listagens, porque a posição no mapa e o índice da
 * região são a mesma coisa. Mexer aqui move as regiões no mapa; a V5 renumerou os
 * dados junto, para cada uma continuar onde estava.
 */
export const CENTROS: [number, number][] = [
  [0, 0], //   1 · centro
  [-1, 3], //  2 · abaixo
  [2, 1], //   3 · direita-baixo
  [3, -2], //  4 · direita-cima
  [1, -3], //  5 · topo
  [-2, -1], // 6 · esquerda-cima
  [-3, 2], //  7 · esquerda-baixo
];

/**
 * As 6 pétalas ao redor do centro de uma região, na mesma lógica das regiões: a 1ª ao
 * centro (slot 0), a 2ª logo abaixo, e daí em diante no sentido anti-horário.
 *
 * <p>O anel de pétalas é girado 30° em relação ao das regiões, então não existe uma
 * vizinha exatamente ao sul — as duas mais baixas são a sudeste e a sudoeste. Escolhi
 * a sudeste, que é o espelho do anel das regiões, cujo hexágono de baixo também cai um
 * pouco à direita.
 */
export const PETALAS: [number, number][] = [
  [0, 1], //   2 · abaixo (sudeste)
  [1, 0], //   3 · direita
  [1, -1], //  4 · direita-cima
  [0, -1], //  5 · esquerda-cima
  [-1, 0], //  6 · esquerda
  [-1, 1], //  7 · esquerda-baixo
];

export const axialParaPixel = (q: number, r: number) => ({
  x: LADO * Math.sqrt(3) * (q + r / 2),
  y: LADO * 1.5 * r,
});

/**
 * O centro, em pixel, do hexágono de uma área — dado o slot da região e o slot da área
 * dentro dela.
 *
 * <p>Slot 0 é o próprio centro da região; 1 a 6 são as pétalas.
 */
export function centroDaArea(slotDaRegiao: number, slotDaArea: number) {
  const [rq, rr] = CENTROS[slotDaRegiao];
  if (slotDaArea === 0) return axialParaPixel(rq, rr);
  const [dq, dr] = PETALAS[slotDaArea - 1];
  return axialParaPixel(rq + dq, rr + dr);
}

export function pontosDoHexagono(cx: number, cy: number, raio: number = LADO): string {
  const pontos: string[] = [];
  for (let i = 0; i < 6; i++) {
    const angulo = (Math.PI / 180) * (60 * i - 90);
    pontos.push(
      `${(cx + raio * Math.cos(angulo)).toFixed(1)},${(cy + raio * Math.sin(angulo)).toFixed(1)}`
    );
  }
  return pontos.join(' ');
}

/** Luminância, para decidir se o texto sobre a cor da região vai escuro ou claro. */
export function luminancia(hex: string): number {
  const c = hex.replace('#', '');
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}
